import { useTranslation } from "react-i18next";
import { Icon } from "../../components/Icon/Icon";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomIconButton } from "../../components/CustomIconButton/CustomIconButton";
import useStyles from "./styles";
import { CustomTypographyLabelValue } from "../../components/CustomTypographyLabelValue/CustomTypographyLabelValue";
import { useRef, useState } from "react";
import { colors } from "../../styles/colors";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { useGetOneEmployee, useUpdateEmployeeAvatar } from "../../api/employee";
import { ModalUpdateCollaborator } from "./ModalUpdateCollaborator/ModalUpdateCollaborator";
import { uploadToStorage } from "../../api/media";
import { CircularProgress } from "@mui/material";
import toast from "react-hot-toast";

type Props = {
  isEditableIcon?: boolean;
  fullInfos?: boolean;
  userId: number;
};

export const ContainerCollaborator: React.FC<Props> = ({
  isEditableIcon,
  fullInfos,
  userId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();
  const { employee } = useGetOneEmployee(fleetId, userId);
  const [openModalCollabEdit, setOpenModalCollabEdit] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Photo shown by default comes from the mobile app (user.avatar);
  // it can also be set/replaced here.
  const avatarUrl = employee?.avatar || null;

  const { mutate: updateAvatar } = useUpdateEmployeeAvatar(fleetId, userId);

  const onCloseModal = () => {
    setOpenModalCollabEdit(false);
  };

  const onPickPhoto = () => {
    // Only fleet managers editing the profile can change the photo.
    if (!isEditableIcon || isUploadingAvatar) return;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("collaborator.avatarInvalidType", "Please select an image"));
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const { url } = await uploadToStorage(file, {
        directory: "user/avatar",
        filename: file.name,
      });
      updateAvatar(url);
    } catch {
      toast.error(t("collaborator.avatarUploadFailed", "Failed to upload photo"));
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  return (
    <>
      <ModalUpdateCollaborator
        employee={employee ?? null}
        open={openModalCollabEdit}
        onClose={onCloseModal}
        onCancel={onCloseModal}
      />
      <div className={classes.containerHeaderLeft}>
        <div className={classes.subContainerHeaderLeft}>
          {/* Images */}
          <div
            className={classes.divImages}
            onClick={onPickPhoto}
            role={isEditableIcon ? "button" : undefined}
            title={
              isEditableIcon
                ? (t("collaborator.changePhoto", "Change photo") as string)
                : undefined
            }
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={t("collaborator.collaborator", "Collaborator") as string}
                className={classes.avatarImg}
              />
            ) : (
              <Icon
                name={"photo"}
                height={"50px"}
                width={"50px"}
                color={colors.greydark}
              />
            )}

            {isEditableIcon && (
              <div className={classes.divImagesOverlay}>
                {isUploadingAvatar ? (
                  <CircularProgress size={22} style={{ color: "#fff" }} />
                ) : (
                  <>
                    <Icon
                      name={"photo"}
                      height={"20px"}
                      width={"20px"}
                      color={"#fff"}
                    />
                    <span>
                      {avatarUrl
                        ? t("collaborator.changePhoto", "Change photo")
                        : t("collaborator.addPhoto", "Add a photo")}
                    </span>
                  </>
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={classes.hiddenFileInput}
              onChange={onFileSelected}
            />
          </div>
          {/* Collaborateurs */}
          <div className={classes.divCollaborator}>
            <div className={classes.divCollaboratorText}>
              <CustomTypography
                text={t("collaborator.collaborator", "Collaborator")}
                variant={"h3"}
                isUppercase
              />
              {isEditableIcon && (
                <CustomIconButton
                  onClick={() => setOpenModalCollabEdit(true)}
                  iconName={"pencil"}
                  widthIcon={"30px"}
                  heightIcon={"30px"}
                />
              )}
            </div>
            <div className={classes.divContainerInfo}>
              <div className={classes.subContainerInfo}>
                <CustomTypographyLabelValue
                  label={t("collaborator.name", "Name")}
                  value={employee?.lastname ?? ""}
                />
                <CustomTypographyLabelValue
                  label={t("collaborator.firstName", "Firstname")}
                  value={employee?.firstname ?? ""}
                />
              </div>
              <div className={classes.subContainerInfo}>
                <CustomTypographyLabelValue
                  label={t("collaborator.service", "Service")}
                  value={employee?.department ?? ""}
                />
                <CustomTypographyLabelValue
                  label={t("collaborator.email", "Email")}
                  value={employee?.email ?? ""}
                />
              </div>
              {fullInfos && (
                <div className={classes.subContainerInfo}>
                  {/* <CustomTypography
                    text={t("collaborator.advantage", "Advantage")}
                    variant="link"
                    lineHeight="9px"
                  />
                  <CustomTypography
                    text={t("collaborator.denomination", "Denomination")}
                    variant="default"
                    fontWeight="bold"
                  />
                  <CustomTypography
                    text={`XXX € ${t("collaborator.monthly", "Monthly")}`}
                    variant="default"
                    fontWeight="bold"
                  /> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
